import {BaseBadge, BaseButton, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useCountries, useCreateCountry, useDeleteCountry, useUpdateCountry} from "@/hooks/useCountries";
import type {Country} from "@/services/countries.service";
import {Pause, Pencil, Play, Trash2} from "lucide-react";
import {useState} from "react";

export function Countries() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);

  const {data, isLoading} = useCountries({page, limit});
  const deleteCountry = useDeleteCountry();
  const updateCountry = useUpdateCountry();

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<Country>[] = [
    {
      key: "name",
      header: "País",
      render: row => (
        <div className="flex items-center gap-2">
          <span className={`fi fi-${row.id} text-xl rounded-sm`} />
          <p className="text-sm font-medium text-(--color-text-primary)">{row.name}</p>
        </div>
      ),
    },
    {
      key: "currency",
      header: "Moneda",
      render: row => (
        <div>
          <p className="text-sm font-medium text-(--color-text-primary)">{row.currencyCode}</p>
          <p className="text-xs text-(--color-text-tertiary)">{row.currency}</p>
        </div>
      ),
    },
    {
      key: "phoneCode",
      header: "Código Tel.",
      render: row => <span className="text-sm text-(--color-text-secondary)">{row.phoneCode}</span>,
    },
    {
      key: "rate",
      header: "Tasa",
      render: row => <span className="text-sm font-mono text-(--color-text-primary)">{row.rate.toLocaleString()}</span>,
    },
    {
      key: "isAvailable",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.isAvailable ? "success" : "neutral"}
          label={row.isAvailable ? "Activo" : "Inactivo"}
          dot
        />
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => setEditingCountry(row)}
            title="Editar"
          >
            <Pencil size={14} />
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => updateCountry.mutate({id: row.id, body: {isAvailable: !row.isAvailable}})}
            title={row.isAvailable ? "Desactivar" : "Activar"}
          >
            {row.isAvailable ? <Pause size={14} /> : <Play size={14} />}
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`¿Eliminar ${row.name}?`)) deleteCountry.mutate(row.id);
            }}
            title="Eliminar"
          >
            <Trash2 size={14} />
          </BaseButton>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Países</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">{pagination.total} países configurados</p>
        </div>
        <BaseButton
          variant="primary"
          size="md"
          onClick={() => setShowCreate(true)}
        >
          + Nuevo país
        </BaseButton>
      </div>

      <BaseCard padding="none">
        <BaseTable<Country>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron países."
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>

      {showCreate && <CreateCountryModal onClose={() => setShowCreate(false)} />}
      {editingCountry && (
        <EditCountryModal
          country={editingCountry}
          onClose={() => setEditingCountry(null)}
        />
      )}
    </div>
  );
}

function EditCountryModal({country, onClose}: {country: Country; onClose: () => void}) {
  const updateCountry = useUpdateCountry();
  const [form, setForm] = useState({
    name: country.name,
    currency: country.currency,
    currencyCode: country.currencyCode,
    phoneCode: country.phoneCode,
    rate: String(country.rate),
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.currency || !form.currencyCode || !form.phoneCode || !form.rate) {
      setError("Todos los campos son requeridos");
      return;
    }
    try {
      await updateCountry.mutateAsync({
        id: country.id,
        body: {
          name: form.name,
          currency: form.currency,
          currencyCode: form.currencyCode.toUpperCase(),
          phoneCode: form.phoneCode,
          rate: parseFloat(form.rate),
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar el país");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">
          <span className={`fi fi-${country.id} mr-2 rounded-sm`} />
          Editar — {country.name}
        </h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-(--color-text-secondary)">Nombre del país</span>
            <input
              placeholder="ej. Argentina"
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Moneda</span>
              <input
                placeholder="ej. Pesos"
                value={form.currency}
                onChange={e => setForm({...form, currency: e.target.value})}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Código moneda</span>
              <input
                placeholder="ej. ARS"
                maxLength={5}
                value={form.currencyCode}
                onChange={e => setForm({...form, currencyCode: e.target.value})}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Tasa de cambio</span>
              <input
                placeholder="ej. 350.50"
                type="number"
                step="any"
                value={form.rate}
                onChange={e => setForm({...form, rate: e.target.value})}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Código teléfono</span>
              <input
                placeholder="ej. +54"
                value={form.phoneCode}
                onChange={e => setForm({...form, phoneCode: e.target.value})}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              />
            </label>
          </div>
          {error && <p className="text-sm text-(--color-danger)">{error}</p>}
          <div className="flex justify-end gap-2 mt-2">
            <BaseButton
              variant="ghost"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </BaseButton>
            <BaseButton
              variant="primary"
              size="md"
              loading={updateCountry.isPending}
            >
              Guardar
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateCountryModal({onClose}: {onClose: () => void}) {
  const createCountry = useCreateCountry();
  const [form, setForm] = useState({name: "", currency: "", currencyCode: "", phoneCode: "", rate: ""});
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.currency || !form.currencyCode || !form.phoneCode || !form.rate) {
      setError("Todos los campos son requeridos");
      return;
    }
    try {
      await createCountry.mutateAsync({
        name: form.name,
        currency: form.currency,
        currencyCode: form.currencyCode.toUpperCase(),
        phoneCode: form.phoneCode,
        rate: parseFloat(form.rate),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear el país");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">Nuevo País</h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-(--color-text-secondary)">Nombre del país</span>
            <input
              placeholder="ej. Argentina"
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Moneda</span>
              <input
                placeholder="ej. Pesos"
                value={form.currency}
                onChange={e => setForm({...form, currency: e.target.value})}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Código moneda</span>
              <input
                placeholder="ej. ARS"
                maxLength={5}
                value={form.currencyCode}
                onChange={e => setForm({...form, currencyCode: e.target.value})}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Tasa de cambio</span>
              <input
                placeholder="ej. 350.50"
                type="number"
                step="any"
                value={form.rate}
                onChange={e => setForm({...form, rate: e.target.value})}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Código teléfono</span>
              <input
                placeholder="ej. +54"
                value={form.phoneCode}
                onChange={e => setForm({...form, phoneCode: e.target.value})}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              />
            </label>
          </div>
          {error && <p className="text-sm text-(--color-danger)">{error}</p>}
          <div className="flex justify-end gap-2 mt-2">
            <BaseButton
              variant="ghost"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </BaseButton>
            <BaseButton
              variant="primary"
              size="md"
              loading={createCountry.isPending}
            >
              Crear
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
}
